// @ts-check

import { CustomizerLayer } from '@theme/custom.customizer-layer';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */

/**
 * Blocks add-to-cart for a broken required step and routes the CTA to that step.
 * It is a hidden layer in the overlay, not a nested dialog, so it never fights the modal state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} body - The body copy line (its `[step]` placeholder takes the step
 *   label).
 *
 * @extends CustomizerLayer<Refs>
 */
class CustomizerIncomplete extends CustomizerLayer {
  requiredRefs = ['body'];

  /** The blocking step's jump index, remembered from {@link show} for the CTA. */
  #stepIndex = -1;

  /**
   * Reveal the dialog for the blocking step, replacing `[step]` with its schema label.
   * This uses the same substitution pattern as the Review rows.
   * @param {number} stepIndex
   * @param {string} label
   */
  show(stepIndex, label) {
    this.#stepIndex = stepIndex;
    const template = this.dataset.body ?? '[step]';
    this.refs.body.textContent = template.replace('[step]', () => label);
    this.openLayer();
  }

  get initialFocus() {
    const cta = this.querySelector('.custom-customizer-confirm__confirm');
    return cta instanceof HTMLElement ? cta : null;
  }

  /** CTA: jump the wizard to the blocking step, then hide. Bound via `on:click="/complete"`. */
  complete = () => {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    const stepIndex = this.#stepIndex;
    // Close first, so the wizard can move focus to the step title without the trap pulling it back.
    this.closeLayer();
    if (stepIndex >= 0) host?.store?.goTo(stepIndex);
  };

  closeLayer() {
    this.#stepIndex = -1;
    super.closeLayer();
  }
}

if (!customElements.get('custom-customizer-incomplete')) {
  customElements.define('custom-customizer-incomplete', CustomizerIncomplete);
}
